import { Flex, Text } from '@chakra-ui/react';
import { FormFeedback } from '@morressier/alembic';
import { FieldLabel } from '../Field.js';
import { AxiosProgressEvent } from 'axios';
import { FormFieldFileUpload } from '@publishing-engine/domain/types/article';
import axiosInstance from 'src/api/client';
import {
  getFileExtension,
  getImageMeta,
  getReferenceName,
  parseUploadError
} from 'src/utils/file';
import { get } from 'lodash';
import { useSubmissionId } from '../../hooks/useSubmissionId.js';
import { getSignedPrivateUploadUrl } from '../../api/queries.js';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { IFileUploaded } from './common/types.js';
import { FieldError, useFormContext } from 'react-hook-form';
import { SingleFileUpload } from './SingleFileUpload.js';
import { getAllowedExtensions } from './FormFieldSingleFileUpload.js';
import { useDuplicateConfirm } from 'src/hooks/useDuplicateConfirm.js';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types.js';

interface IFormFieldSingleFileUploadProps {
  config: Omit<FormFieldFileUpload, 'component'>;
  methods: any;
  submissionContextId: string;
  referenceName?: string;
  isEditingAllowed?: boolean;
  submissionId?: string;
  FiledName?: string;
  submissionFields?: ISubmissionFieldsResponse;
}

const FormFieldFigureSingleFileUpload: React.FC<
  IFormFieldSingleFileUploadProps
> = ({
  config,
  referenceName,
  submissionId: staticSubmissionId,
  isEditingAllowed = true,
  FiledName,
  submissionFields
}) => {
  const {
    setError,
    clearErrors,
    formState: { errors },
    register,
    setValue,
    watch,
    getValues
  } = useFormContext();
  const {
    caseDataFields: { main: field },
    params: { validations }
  } = config;
  const { checkIsDuplicateFile, duplicateFileModal } = useDuplicateConfirm();
  const fieldError = get(errors, field) as FieldError | undefined;

  const schemaError = get(errors, `${field.replace('.file', '')}`) as
    | FieldError
    | undefined;

  const mainError =
    fieldError ||
    (schemaError?.type === 'not'
      ? {
          type: schemaError.type,
          message: schemaError.message
        }
      : undefined);

  const error = errors[field];
  const file = watch(field);

  const submissionId = staticSubmissionId ?? useSubmissionId();
  const getUploadUrl = (extension: string) => {
    if (!submissionId) {
      throw new Error('FormFieldSingleFileUpload: no submission id. ' + field);
    }
    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const uploadFile = async (file: File) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: referenceName || getReferenceName(file.name),
      progress: 0,
      isUploading: true,
      extension: fileExtension.toLowerCase(),
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      setValue(field, fileToUpload);
    };

    try {
      setValue(field, fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        extension: fileExtension.toLowerCase()
      };

      setValue(field, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);
      setError(field, {
        type: 'upload-error',
        message
      });
    } finally {
      setValue(field, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  const deleteUploadedFile = (id: string) =>
    axiosInstance.delete(`/asset/${id}`);

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);
    const selected = acceptedFiles[0];
    const canupload = await checkIsDuplicateFile(
      { fieldName: field, name: selected?.name || '' },
      submissionFields ?? getValues()?.fileSet
    );
    if (!selected) return;
    if (!canupload) return;
    const metadata = await getImageMeta(selected);
    const metafield = field.replace('.file', '.metadata');
    setValue(metafield, metadata);
    file?.fileId && deleteUploadedFile(file.fileId);
    selected && (await uploadFile(selected));
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    if (rejectedFiles[0]) {
      const rejectedFileError = rejectedFiles[0].errors[0];
      setValue(field, {
        name: rejectedFiles[0].file.name,
        url: '',
        isUploading: false,
        progress: 0,
        referenceName: '',
        extension: ''
      });
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(field, {
          type: 'upload-error',
          message: `Only ${getAllowedExtensions(
            validations.allowedFileFormats
          ).join(', ')} formats allowed`
        });
      }
      if (rejectedFileError?.code === ErrorCode.FileTooLarge) {
        setError(field, {
          type: 'upload-error',
          message: `Max file size reached. Upload file lesser than or equal to ${validations.maxSizeMB}MB`
        });
      }
      if (rejectedFileError?.code === ErrorCode.TooManyFiles) {
        setError(field, {
          type: 'upload-error',
          message: rejectedFileError.message
        });
      }
    }
  };

  return (
    <>
      <Flex flexDirection="column" gap={2} width="100%">
        {isEditingAllowed && (
          <Flex flexDirection="column" bgColor="surfacePrimaryFill">
            <SingleFileUpload
              {...register(field)}
              uploadButtonText={
                file && file.url !== '' ? file.name : 'Select file'
              }
              onDrop={onDrop}
              onDropRejected={onDropRejected}
              maxSize={validations.maxSizeMB * 1000000}
              isInvalid={Boolean(error?.message)}
              accept={validations.allowedFileFormats}
              file={file?.url ? file : undefined}
              multiple={false}
            />
          </Flex>
        )}

        {!isEditingAllowed && (
          <FieldLabel
            isRequired={validations.required}
            label="Attached File"
            subText=""
          />
        )}
        {/* FILE-RELATED ERRORS */}
        {file && (
          <>
            {file.isUploading ? (
              <Text textStyle="textContext">Uploading... {file.progress}%</Text>
            ) : mainError && mainError.type !== 'not' ? (
              <FormFeedback variant="error">
                {(mainError as any)?.url?.type === 'minLength'
                  ? `Upload a valid ${FiledName ?? 'figure'} file`
                  : mainError.message}
              </FormFeedback>
            ) : null}
          </>
        )}

        {!file && mainError?.message && (
          <FormFeedback variant="error">
            Upload a valid {FiledName ?? 'figure'} file
          </FormFeedback>
        )}
      </Flex>
      {duplicateFileModal}
    </>
  );
};

export default FormFieldFigureSingleFileUpload;
